import React, { useState, useEffect, useCallback } from 'react';
import { Coins, RefreshCw, Sparkles, ShoppingCart } from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// 💰 CreditsBanner — bannière de crédits affichée en haut
// de chaque module à crédits (Pub Vidéos, Marketing Studio, VTry-on)
// ============================================================

const SERVICE_META = {
  video_ads:        { label: 'TeckiA Pub Vidéos' },
  marketing_studio: { label: 'Marketing Studio' },
  teckia_vtryon:    { label: 'TeckiA VTry-On' },
};

export default function CreditsBanner({ serviceId, onBuy }) {
  const [credits, setCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/credits-summary`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      const data = await res.json();
      if (data.success) setCredits(data.credits[serviceId] || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [serviceId]);

  useEffect(() => { load(); }, [load]);

  const total = credits ? (credits.images || 0) + (credits.videos || 0) : 0;
  const isEmpty = !loading && total === 0;

  return (
    <div className={`relative overflow-hidden rounded-2xl p-[1.5px] mb-6 ${
      isEmpty
        ? 'bg-gradient-to-r from-orange-500 via-red-500 to-pink-500'
        : 'bg-gradient-to-r from-indigo-500 via-purple-500 to-fuchsia-500'
    }`}>
      <div className="relative rounded-2xl bg-slate-950/95 px-4 py-3.5 md:px-6 md:py-4">
        {/* Glow décoratif */}
        <div className={`absolute -top-10 -right-10 w-40 h-40 rounded-full blur-3xl opacity-30 ${
          isEmpty ? 'bg-orange-500' : 'bg-purple-500'
        }`} />

        <div className="relative flex flex-wrap items-center gap-3 md:gap-5">
          {/* Icône + titre */}
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 md:w-11 md:h-11 rounded-xl flex items-center justify-center ${
              isEmpty
                ? 'bg-gradient-to-br from-orange-500 to-red-500'
                : 'bg-gradient-to-br from-indigo-500 to-fuchsia-500'
            }`}>
              <Coins className="w-5 h-5 md:w-6 md:h-6 text-white" />
            </div>
            <div>
              <div className="text-white font-bold text-sm md:text-base leading-tight">
                {isEmpty ? 'Aucun crédit disponible' : 'Crédits disponibles'}
              </div>
              <div className="text-gray-400 text-xs">{SERVICE_META[serviceId]?.label || 'Module'}</div>
            </div>
          </div>

          {/* Compteurs */}
          <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-[180px]">
            {loading ? (
              <span className="text-gray-400 text-sm animate-pulse">Chargement...</span>
            ) : (
              <>
                {credits?.images !== undefined && (
                  <CreditChip emoji="📸" value={credits.images} unit="images" empty={isEmpty} />
                )}
                {credits?.videos !== undefined && (
                  <CreditChip emoji="🎬" value={credits.videos} unit="vidéos" empty={isEmpty} />
                )}
              </>
            )}
            <button onClick={load} title="Actualiser"
              className="p-1.5 rounded-lg bg-white/5 hover:bg-white/15 text-gray-400 hover:text-white transition-all">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Bouton achat */}
          {onBuy && (
            <button
              onClick={onBuy}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm text-white transition-all hover:scale-105 shadow-lg ${
                isEmpty
                  ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-400 hover:to-red-400 animate-pulse'
                  : 'bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:from-indigo-500 hover:to-fuchsia-500'
              }`}
            >
              <ShoppingCart className="w-4 h-4" />
              Acheter des crédits
            </button>
          )}
        </div>

        {isEmpty && (
          <p className="relative text-orange-300/90 text-xs mt-2 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Achetez des crédits pour utiliser ce module — vos créations vous attendent !
          </p>
        )}
      </div>
    </div>
  );
}

const CreditChip = ({ emoji, value, unit, empty }) => (
  <div className={`flex items-center gap-2 px-3 py-1.5 rounded-xl border ${
    value > 0
      ? 'bg-white/10 border-white/15'
      : 'bg-red-500/10 border-red-500/25'
  }`}>
    <span className="text-base">{emoji}</span>
    <span className={`font-extrabold text-lg md:text-xl leading-none ${
      value > 0 ? 'text-white' : 'text-red-400'
    }`}>{value}</span>
    <span className="text-gray-400 text-xs">{unit}</span>
  </div>
);
